import {
  itemApiField,
  apiField,
  constructFromApiData,
  getApiPostData,
  getDiff,
  itemArrayApiField,
  decimalApiField,
} from "../ResourceItemMetadata";
import { RevisionItem } from "../RevisionItem";
import { createRevisionClassResource } from "../../revision";
import { Region } from "./Region";
import { ResourceItem } from "../ResourceItem";
import { getDumpAction } from "../../dump";
import MegaraxClient from "../../megaraxClient";
import Decimal from "decimal.js";

export const stopienPozyskaniaOptions = [
  "ODMAWIA",
  "NIEROZPOZNANY",
  "ROZPOZNANY",
  "POZYSKIWANY",
  "NIEWYKORZYSTANY",
  "WYKORZYSTANY",
  "WYŁĄCZNIE",
];

type StopienPozyskania =
  | "ODMAWIA"
  | "NIEROZPOZNANY"
  | "ROZPOZNANY"
  | "POZYSKIWANY"
  | "NIEWYKORZYSTANY"
  | "WYKORZYSTANY"
  | "WYŁĄCZNIE";

type LabeledOption = {
  value: any;
  label: string;
};

export const obrotOptions: LabeledOption[] = [
  { value: 0, label: "<20" },
  { value: 20, label: "20-49" },
  { value: 50, label: "50-99" },
  { value: 100, label: "100-199" },
  { value: 200, label: "200-499" },
  { value: 500, label: "500-1000" },
  { value: 1000, label: ">1000" },
];

type Obrot = 0 | 20 | 50 | 100 | 200 | 500 | 1000;

export const powierzchniaOptions: LabeledOption[] = [
  { value: 0, label: "<50" },
  { value: 50, label: "50-99" },
  { value: 100, label: "100-199" },
  { value: 200, label: "200-499" },
  { value: 500, label: "500-1000" },
  { value: 1000, label: ">1000" },
];
type Powierzchnia = 0 | 50 | 100 | 200 | 500 | 1000;

export class CustomerAddress extends RevisionItem {
  @apiField()
  name!: string;

  @apiField()
  abbreviatedName!: string;
  @apiField()
  fullName!: string;

  @itemApiField(Region)
  region!: Region;

  @itemApiField(ResourceItem)
  chain: ResourceItem | null = null;

  @apiField()
  firma!: string;
  @apiField()
  formaPrawna?: string;
  @apiField()
  nip?: string;
  @apiField()
  locality!: string;
  @apiField()
  postCode!: string;
  @apiField()
  street!: string;
  @apiField()
  houseNumber?: string;
  @apiField()
  phoneNumber?: string;

  @apiField()
  stopienPozyskania: StopienPozyskania | null = null;
  @apiField()
  czestotliwoscWizyt: number | null = null;
  @apiField()
  powierzchniaSklepu: Powierzchnia | null = null;
  @apiField()
  powierzchniaMagazynu: Powierzchnia | null = null;
  @apiField()
  obrot: Obrot | null = null;
  @apiField()
  liczbaPracownikow: number | null = null;
  @itemArrayApiField(ResourceItem)
  branza: ResourceItem[] = [];

  @decimalApiField()
  lat: Decimal | null = null;
  @decimalApiField()
  lng: Decimal | null = null;

  @apiField({ readOnly: true })
  metersFromHq: number | null = null;
  @apiField({ readOnly: true })
  distanceFromHqText: string | null = null;
  @apiField({ readOnly: true })
  secondsFromHq: number | null = null;
  @apiField({ readOnly: true })
  timeFromHqText: string | null = null;

  constructor(data?: any) {
    super(data);

    if (data) {
      constructFromApiData(this, data);
    }
  }

  toString() {
    return this.id!.toString();
  }

  getPostData() {
    return {
      ...getApiPostData(this),
      ...super.getPostData(),
      chain: this.chain ? { id: this.chain.id } : null,
    };
  }

  getDiff(other: this) {
    return getDiff(this, other);
  }
}

export interface SimpleCustomerAddress extends RevisionItem {
  lat: string;
  lng: string;
  region: string;
  locality: string;
  postCode: string;
  street: string;
  houseNumber?: string;
}

const dataDumpLabels = [
  { key: "id", header: "id" },
  { key: "name", header: "nazwa" },
  { key: "region.str", header: "region" },
  { key: "abbreviatedName", header: "skrocona nazwa" },
  { key: "fullName", header: "pelna nazwa" },
  { key: "locality", header: "miejscowosc" },
  { key: "postCode", header: "kod pocztowy" },
  { key: "street", header: "street" },
  { key: "houseNumber", header: "numer budynku" },
  { key: "phoneNumber", header: "numer telefonu" },
  { key: "firma", header: "firma" },
  { key: "formaPrawna", header: "forma prawna" },
  { key: "nip", header: "nip" },
  { key: "stopienPozyskania", header: "stopien pozyskania" },
  { key: "czestotliwoscWizyt", header: "czestotliwosc wizyt" },
  { key: "obrot", header: "obrot" },
  { key: "powierzchniaSklepu", header: "powierzchnia sklepu" },
  { key: "powierzchniaMagazynu", header: "powierzchnia magazynu" },
  { key: "liczbaPracownikow", header: "liczba pracownikow" },
  { key: "distanceFromHqMeters", header: "odleglosc_od_megaronu_metry" },
  { key: "timeFromHqSeconds", header: "czas_od_megaronu_sekundy" },
  { key: "chain.str", header: "siec" },
];

export const customerAddressResource = (client: MegaraxClient) => ({
  ...createRevisionClassResource<CustomerAddress, SimpleCustomerAddress>(
    "crm/customerAddress/",
    CustomerAddress,
  )(client),
  dump: getDumpAction("crm/customerAddress/", dataDumpLabels)(client),
});
